import { computed, ref, watch } from 'vue';

import { usePleasedChat } from '../pleased-chat-controller/usePleasedChatController';
import { usePleasedTicket } from '../pleased-chat-controller/usePleasedTicket';
import { usePleasedVoiceChat } from '../pleased-chat-controller/usePleasedVoiceChat';

import type { PleasedSupportRequest } from './usePleasedSupport.types';
import { isChatRequest, isTicketRequest, isVoiceRequest } from './usePleasedSupport.lib';

export function usePleasedSupport() {
  const chat = usePleasedChat();
  const voice = usePleasedVoiceChat();
  const ticket = usePleasedTicket();

  const error = ref<unknown>();

  const resolveAggregatedSupportError = () =>
    chat.errorCreatingChat.value ||
    chat.errorLoadingExistingChat.value ||
    voice.errorCreatingVoice.value ||
    ticket.errorCreatingTicket.value;

  async function connect(request: PleasedSupportRequest) {
    // Reset the errors.
    chat.resetError();
    voice.resetError();
    ticket.resetError();

    if (isChatRequest(request)) return chat.createChat(request.payload);
    if (isVoiceRequest(request)) return voice.createVoiceChat(request.payload);
    if (isTicketRequest(request)) return ticket.createTicket(request.payload);
  }

  watch(
    [chat.errorCreatingChat, chat.errorLoadingExistingChat, voice.errorCreatingVoice, ticket.errorCreatingTicket],
    () => {
      error.value = resolveAggregatedSupportError() ?? null;
    }
  );

  function resetError() {
    error.value = null;
  }

  function reset() {
    resetError();
    chat.reset();
    voice.reset();
    ticket.reset();
  }

  const isLoading = computed(
    () => chat.isChatSessionLoading.value || voice.isCreatingVoice.value || ticket.isCreatingTicket.value
  );

  return {
    chat: { ...chat },
    voice: { ...voice },
    ticket: { ...ticket },
    connect,
    resetError,
    supportError: error,
    supportLoading: isLoading,
    reset,
  };
}
